// src/pages/ApplyPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaEnvelope,
  FaFileAlt,
  FaHome,
  FaMoneyBillWave,
  FaUserGraduate,
  FaUsers
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import FormField from '../components/common/FormField';
import Card from '../components/common/Card';
import COURSES_DATA from '../data/coursesdata';
import useForm from '../hooks/useForm';
import { setupRevealAnimations } from '../utils/helpers';
import {
  createRegistrationPaymentCode,
  fetchRegistrationPaymentStatus,
  submitRegistration
} from '../services/registrationService';

const educationOptions = [
  'University Graduate',
  'University Student',
  'Technical and Vocational Education',
  'High School Pupil',
  'Junior School Pupil',
  'No formal Education'
];

const careerPathOptions = [
  'Mechanical Engineering',
  'Electrical and Electronics Engineering',
  'Civil Engineering and related fields',
  'Architecture',
  'Pure and Applied Sciences',
  'Medical Sciences and related fields',
  'Commercial, Management and Public Administration',
  'Arts and Humanities',
  'Technical and Vocational'
];

const institutionOptions = [
  'FBC',
  'COMAHS',
  'IPAM',
  'MMTU',
  'IMAT College',
  'GTI',
  'Njala',
  'Limkokwing',
  'AI Professional College',
  'Blue Crest',
  'UNIMTECH',
  'SKY Vision College',
  'I am a high school pupil',
  'I am now working',
  'Central University',
  'Other Colleges/institutions'
];

const computerSkillOptions = [
  'Beginner',
  'Basic',
  'Intermediate',
  'Advanced'
];

const heardFromOptions = [
  'Through a Friend',
  'Social Media',
  'School or University',
  'Afrovate Tech Hub Event',
  'WhatsApp',
  'Radio or TV',
  'Website',
  'Other'
];

const REGISTRATION_FEE_AMOUNT = 4.50;

const parseFeeAmount = (fee) => Number(String(fee).replace(/[^\d.]/g, '')) || 0;

const formatMoney = (amount) => `NLe ${Number(amount).toLocaleString('en')}`;
const createUssdTelHref = (ussdCode) => `tel:${encodeURIComponent(String(ussdCode).replace(/\s+/g, ''))}`;
const FINAL_PAYMENT_STATUSES = ['processed', 'completed'];
const STOP_POLLING_PAYMENT_STATUSES = [...FINAL_PAYMENT_STATUSES, 'expired', 'cancelled'];

const ApplyPage = () => {
  const [step, setStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedRegistration, setSubmittedRegistration] = useState(null);
  const [registrationSnapshot, setRegistrationSnapshot] = useState(null);
  const [courseIdsToPay, setCourseIdsToPay] = useState([]);
  const [paymentCode, setPaymentCode] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentStatusError, setPaymentStatusError] = useState('');
  const [isCreatingPaymentCode, setIsCreatingPaymentCode] = useState(false);
  const formContainerRef = useRef(null);

  const courses = useMemo(
    () => Object.values(COURSES_DATA).flatMap((category) => category.courses),
    []
  );

  const courseOptions = courses.map((course) => ({
    id: course.id,
    title: course.title,
    fee: course.trainingFee
  }));

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
    setErrors
  } = useForm(
    {
      fullName: '',
      dateOfBirth: '',
      gender: '',
      otherGender: '',
      passportPhoto: null,
      whatsappNumber: '',
      email: '',
      permanentAddress: '',
      freetownArea: '',
      educationLevel: '',
      careerPath: '',
      institution: '',
      computerSkill: '',
      selectedCourses: [],
      heardFrom: '',
      referrerName: '',
      expectations: '',
      declaration: false
    },
    (formValues) => validateStep(formValues, step),
    async (formValues) => {
      const response = await submitRegistration(formValues);
      setSubmittedRegistration(response.registration);
      setRegistrationSnapshot({
        fullName: formValues.fullName,
        email: formValues.email,
        selectedCourses: formValues.selectedCourses,
        registrationNumber: response.registration?.registration_number
      });
      setCourseIdsToPay([]);
      setPaymentCode(null);
      setPaymentError('');
      setPaymentStatusError('');
      setStep(5);
      scrollToForm();
    }
  );

  useEffect(() => {
    setupRevealAnimations();
    window.scrollTo(0, 0);

    const urlParams = new URLSearchParams(window.location.search);
    const programId = urlParams.get('program');

    if (programId && courses.some((course) => course.id === programId)) {
      setFieldValue('selectedCourses', [programId]);
    }
    // Only run on mount so URL preselection does not overwrite user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToForm = () => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  function validateStep(formValues, currentStep) {
    const validationErrors = {};
    const requiredByStep = {
      2: [
        'fullName',
        'dateOfBirth',
        'gender',
        'passportPhoto',
        'whatsappNumber',
        'email',
        'permanentAddress',
        'freetownArea'
      ],
      3: ['educationLevel', 'careerPath', 'institution', 'computerSkill'],
      4: ['heardFrom']
    };

    (requiredByStep[currentStep] || []).forEach((field) => {
      if (!formValues[field]) {
        validationErrors[field] = 'This field is required';
      }
    });

    if (currentStep === 2 && formValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
      validationErrors.email = 'Please enter a valid email address';
    }

    if (currentStep === 2 && formValues.whatsappNumber && !/^[+]?[\d\s-]{7,15}$/.test(formValues.whatsappNumber)) {
      validationErrors.whatsappNumber = 'Please enter a valid WhatsApp number';
    }

    if (currentStep === 2 && formValues.gender === 'Other' && !formValues.otherGender) {
      validationErrors.otherGender = 'Please specify your gender';
    }

    if (currentStep === 4) {
      if (!formValues.selectedCourses || formValues.selectedCourses.length === 0) {
        validationErrors.selectedCourses = 'Select at least one course';
      }

      if (formValues.selectedCourses?.length > 4) {
        validationErrors.selectedCourses = 'Select not more than 4 courses';
      }

      if (!formValues.declaration) {
        validationErrors.declaration = 'You must confirm the declaration';
      }
    }

    return validationErrors;
  }

  const handleNextStep = () => {
    const validationErrors = validateStep(values, step);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setStep((currentStep) => Math.min(currentStep + 1, 5));
    scrollToForm();
  };

  const handlePreviousStep = () => {
    setStep((currentStep) => Math.max(currentStep - 1, 1));
    scrollToForm();
  };

  const handleCourseToggle = (courseId) => {
    const selectedCourses = values.selectedCourses || [];
    const nextCourses = selectedCourses.includes(courseId)
      ? selectedCourses.filter((id) => id !== courseId)
      : selectedCourses.length < 4
        ? [...selectedCourses, courseId]
        : selectedCourses;

    setFieldValue('selectedCourses', nextCourses);

    if (errors.selectedCourses) {
      setErrors((currentErrors) => ({ ...currentErrors, selectedCourses: undefined }));
    }
  };

  const handlePaymentCourseToggle = (courseId) => {
    setPaymentCode(null);
    setPaymentError('');
    setPaymentStatusError('');
    setCourseIdsToPay((currentCourseIds) => (
      currentCourseIds.includes(courseId)
        ? currentCourseIds.filter((id) => id !== courseId)
        : [...currentCourseIds, courseId]
    ));
  };

  const handleCreatePaymentCode = async () => {
    if (!submittedRegistration?.id) {
      setPaymentError('Please submit the registration form before creating a payment code.');
      return;
    }

    setIsCreatingPaymentCode(true);
    setPaymentError('');
    setPaymentStatusError('');

    try {
      const payment = await createRegistrationPaymentCode(submittedRegistration.id, courseIdsToPay);
      setPaymentCode(payment);
    } catch (requestError) {
      setPaymentError(requestError.response?.data?.message || 'Unable to create payment code.');
    } finally {
      setIsCreatingPaymentCode(false);
    }
  };

  useEffect(() => {
    if (!paymentCode?.id || STOP_POLLING_PAYMENT_STATUSES.includes(paymentCode.status)) {
      return undefined;
    }

    let isActive = true;

    const pollPaymentStatus = async () => {
      try {
        const payment = await fetchRegistrationPaymentStatus(paymentCode.id);

        if (!isActive) {
          return;
        }

        setPaymentCode(payment);
        setPaymentStatusError('');
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        setPaymentStatusError(requestError.response?.data?.message || 'Unable to check payment status automatically.');
      }
    };

    const timeoutId = window.setTimeout(pollPaymentStatus, 3000);
    const intervalId = window.setInterval(pollPaymentStatus, 8000);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [paymentCode?.id, paymentCode?.status]);

  const handleStartNewRegistration = () => {
    window.location.reload();
  };

  const renderSelect = ({ label, name, value, options, error, required = true }) => (
    <div className="mb-4">
      <label className="block mb-2 text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={handleChange}
        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
          error ? 'border-red-500 bg-red-50' : 'border-gray-300'
        }`}
      >
        <option value="">Choose</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );

  const renderIntro = () => (
    <div className="max-w-3xl mx-auto">
      <Heading level={3} className="mb-4">
        Registration for Afrovate Tech Hub 2-Months Skills Training Program
      </Heading>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mb-8">
        <p className="text-gray-700 mb-3">
          This is the registration form for Cohort-2 of the Afrovate Tech Hub training program.
        </p>
        <p className="text-gray-700 mb-3">
          Thank you for expressing interest in the intensive hands-on training offered at Afrovate Tech Hub. We are super excited to welcome you on-board. For common understanding between trainees and management, the following terms and conditions apply to all trainees on submitting this registration form.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-gray-700">
          <li>The registration fee of NLe 450 cannot be refunded after this registration form has been submitted.</li>
          <li>All trainees must pay their fee for each course opted for before the beginning of the training on the 1st of July 2026.</li>
          <li>No part payment of fees is accepted.</li>
          <li>On submitting this form, you agree to Afrovate Tech Hub Privacy Terms and Conditions for using your personal information, photo(s), and video(s) for education, information, and promotional materials without sharing with any third-party.</li>
          <li>Courses are delivered in-person and all registered trainees consent to being physically present at 20A Hennessy Street Kingtom in Freetown for classes. There is no online option available for this training.</li>
          <li>All trainees commit to taking 4 - 6 hours of classes per week for every course opted for.</li>
          <li>There are two shifts of classes: morning and evening. Trainees can choose the appropriate shift for classes depending on their schedules and the available timetable.</li>
          <li>The duration of the training is 8 weeks (two months), at the end of which a certificate of participation will be offered to successful trainees who took at least 70% of the classes and passed the final exams or submitted their final project.</li>
          <li>A certification fee will be required at the end of the 2 months training. The exact certificate fee, which depends on the current cost of consumables, will be announced towards the end of the course.</li>
          <li>Other terms and conditions will be negotiated between Afrovate management and trainees in due course.</li>
        </ul>
        <p className="text-gray-700 mt-4">
          Fields marked with <span className="text-red-500 font-semibold">*</span> are required. You may select up to 4 courses for effectiveness.
        </p>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" size="lg" onClick={handleNextStep} icon={<FaArrowRight />} iconPosition="right">
          Start Registration
        </Button>
      </div>
    </div>
  );

  const renderPersonalInformation = () => (
    <div className="max-w-3xl mx-auto">
      <Heading level={3} className="mb-4">Personal Information</Heading>
      <p className="text-gray-600 mb-6">
        This information will be used on your student ID Card and Certificate. Please be as accurate as possible.
      </p>

      <FormField
        label="Full Name"
        name="fullName"
        value={values.fullName}
        onChange={handleChange}
        error={errors.fullName}
        placeholder="Starting with surname. Do not abbreviate your name"
        required
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Date of Birth"
          type="date"
          name="dateOfBirth"
          value={values.dateOfBirth}
          onChange={handleChange}
          error={errors.dateOfBirth}
          required
        />
        {renderSelect({
          label: 'Gender',
          name: 'gender',
          value: values.gender,
          options: ['Male', 'Female', 'Prefer not to say', 'Other'],
          error: errors.gender
        })}
      </div>

      {values.gender === 'Other' && (
        <FormField
          label="Please specify"
          name="otherGender"
          value={values.otherGender}
          onChange={handleChange}
          error={errors.otherGender}
          required
        />
      )}

      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-800">
          Please upload your Passport Picture <span className="text-red-500">*</span>
        </label>
        <input
          type="file"
          name="passportPhoto"
          accept="image/*"
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.passportPhoto ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
        />
        {values.passportPhoto && (
          <p className="mt-1 text-sm text-gray-500">{values.passportPhoto.name}</p>
        )}
        {errors.passportPhoto && <p className="mt-1 text-sm text-red-600">{errors.passportPhoto}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="WhatsApp Number"
          name="whatsappNumber"
          value={values.whatsappNumber}
          onChange={handleChange}
          error={errors.whatsappNumber}
          required
        />
        <FormField
          label="Email Address"
          type="email"
          name="email"
          value={values.email}
          onChange={handleChange}
          error={errors.email}
          required
        />
      </div>

      <FormField
        label="Permanent Address"
        name="permanentAddress"
        value={values.permanentAddress}
        onChange={handleChange}
        error={errors.permanentAddress}
        required
      />

      {renderSelect({
        label: 'Which part of Freetown are you From?',
        name: 'freetownArea',
        value: values.freetownArea,
        options: ['Western', 'Eastern', 'Central', 'I live out of Freetown'],
        error: errors.freetownArea
      })}

      <div className="flex justify-between mt-8">
        <Button variant="outline-primary" size="lg" onClick={handlePreviousStep}>Back</Button>
        <Button variant="primary" size="lg" onClick={handleNextStep} icon={<FaArrowRight />} iconPosition="right">
          Continue
        </Button>
      </div>
    </div>
  );

  const renderAcademicBackground = () => (
    <div className="max-w-3xl mx-auto">
      <Heading level={3} className="mb-4">Academic & Background Information</Heading>
      <p className="text-gray-600 mb-6">
        This section helps us understand your ability to take the Afrovate training for high performance skills.
      </p>

      {renderSelect({
        label: 'What is your level of Education?',
        name: 'educationLevel',
        value: values.educationLevel,
        options: educationOptions,
        error: errors.educationLevel
      })}

      {renderSelect({
        label: 'Which of the following best describes your career path?',
        name: 'careerPath',
        value: values.careerPath,
        options: careerPathOptions,
        error: errors.careerPath
      })}

      {renderSelect({
        label: 'Which Institution are you from?',
        name: 'institution',
        value: values.institution,
        options: institutionOptions,
        error: errors.institution
      })}

      {renderSelect({
        label: 'How good is your basic computer skill?',
        name: 'computerSkill',
        value: values.computerSkill,
        options: computerSkillOptions,
        error: errors.computerSkill
      })}

      <div className="flex justify-between mt-8">
        <Button variant="outline-primary" size="lg" onClick={handlePreviousStep}>Back</Button>
        <Button variant="primary" size="lg" onClick={handleNextStep} icon={<FaArrowRight />} iconPosition="right">
          Continue
        </Button>
      </div>
    </div>
  );

  const renderTrainingSelection = () => (
    <div className="max-w-3xl mx-auto">
      <Heading level={3} className="mb-4">Afrovate Training Offered</Heading>
      <p className="text-gray-600 mb-6">
        Select not more than 4 courses for effectiveness.
      </p>

      <div className="mb-6">
        <label className="block mb-3 text-sm font-medium text-gray-800">
          Which of the following courses are you interested in? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          {courseOptions.map((course) => {
            const checked = values.selectedCourses.includes(course.id);
            const disabled = !checked && values.selectedCourses.length >= 4;

            return (
              <label
                key={course.id}
                className={`flex items-start rounded-lg border p-4 transition-colors ${
                  checked ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200 bg-white'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => handleCourseToggle(course.id)}
                  className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="ml-3 flex-1">
                  <span className="block font-medium text-gray-800">{course.title}</span>
                  <span className="block text-sm text-gray-500">Training fee: {course.fee}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <span className={errors.selectedCourses ? 'text-red-600' : 'text-gray-500'}>
            {errors.selectedCourses || `${values.selectedCourses.length}/4 selected`}
          </span>
        </div>
      </div>

      {renderSelect({
        label: 'How did you first hear about Afrovate Tech Hub?',
        name: 'heardFrom',
        value: values.heardFrom,
        options: heardFromOptions,
        error: errors.heardFrom
      })}

      <FormField
        label="If you were referred, please provide the name of the person who referred you."
        name="referrerName"
        value={values.referrerName}
        onChange={handleChange}
        error={errors.referrerName}
        required={false}
      />

      <FormField
        label="Please tell us what you expect to gain from the training program."
        type="textarea"
        name="expectations"
        value={values.expectations}
        onChange={handleChange}
        error={errors.expectations}
        inputClassName="min-h-[140px]"
      />

      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start">
          <input
            id="declaration"
            name="declaration"
            type="checkbox"
            checked={values.declaration}
            onChange={handleChange}
            className={`mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary ${
              errors.declaration ? 'border-red-500' : ''
            }`}
          />
          <label htmlFor="declaration" className="ml-3 text-sm text-gray-700">
            <span className="font-medium">Declaration <span className="text-red-500">*</span></span>
            <span className="block mt-1">
              I confirm that all the information provided in this form are true and credible to the best of my knowledge. I also pledge my commitment to abiding by the terms and policies of Afrovate Tech Hub.
            </span>
          </label>
        </div>
        {errors.declaration && <p className="mt-1 text-sm text-red-600">{errors.declaration}</p>}
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline-primary" size="lg" onClick={handlePreviousStep}>Back</Button>
        <Button variant="primary" size="lg" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </div>
  );

  const renderPaymentStep = () => {
    const selectedCourseIds = registrationSnapshot?.selectedCourses || [];
    const payableCourses = courseOptions.filter((course) => selectedCourseIds.includes(course.id));
    const selectedCourseFees = payableCourses
      .filter((course) => courseIdsToPay.includes(course.id))
      .reduce((total, course) => total + parseFeeAmount(course.fee), 0);
    const totalToPay = REGISTRATION_FEE_AMOUNT + selectedCourseFees;
    const paymentStatus = paymentCode?.status || 'pending';
    const paymentConfirmed = FINAL_PAYMENT_STATUSES.includes(paymentStatus);

    return (
      <div className="max-w-3xl mx-auto">
        <Heading level={3} className="mb-4">Payment</Heading>
        <p className="text-gray-600 mb-6">
          Your registration details have been saved. Generate a Monime payment code to pay the compulsory registration fee. You can also add any selected course fees you want to pay now.
        </p>

        {registrationSnapshot?.registrationNumber && (
          <div className="bg-secondary bg-opacity-10 border border-secondary border-opacity-40 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">Registration ID</p>
            <p className="text-2xl font-bold text-primary-dark">{registrationSnapshot.registrationNumber}</p>
          </div>
        )}

        <div className="space-y-4 mb-6">
          <div className="rounded-lg border border-primary bg-primary bg-opacity-5 p-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                checked
                readOnly
                className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <div className="ml-3 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="font-semibold text-gray-800">Registration fee</span>
                  <span className="font-bold text-primary">{formatMoney(REGISTRATION_FEE_AMOUNT)}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Required for every trainee before the application can be processed.</p>
              </div>
            </div>
          </div>

          {payableCourses.length > 0 && (
            <div>
              <label className="block mb-3 text-sm font-medium text-gray-800">
                Course fees to pay now
              </label>
              <div className="space-y-3">
                {payableCourses.map((course) => {
                  const checked = courseIdsToPay.includes(course.id);

                  return (
                    <label
                      key={course.id}
                      className={`flex items-start rounded-lg border p-4 transition-colors ${
                        checked ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200 bg-white'
                      } cursor-pointer hover:border-primary`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handlePaymentCourseToggle(course.id)}
                        className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="ml-3 flex-1">
                        <span className="block font-medium text-gray-800">{course.title}</span>
                        <span className="block text-sm text-gray-500">Training fee: {course.fee}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between text-lg font-bold text-primary-dark">
            <span>Total to pay</span>
            <span>{formatMoney(totalToPay)}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            This payment code includes the compulsory registration fee and the optional course fees selected above.
          </p>
        </div>

        {paymentError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
            {paymentError}
          </div>
        )}

        {paymentStatusError && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4 mb-6">
            {paymentStatusError}
          </div>
        )}

        {paymentCode ? (
          <div className={`${paymentConfirmed ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-6 mb-6`}>
            <div className="flex items-start gap-3">
              <FaCheckCircle className={`${paymentConfirmed ? 'text-green-600' : 'text-primary'} text-2xl mt-1`} />
              <div>
                <h4 className={`font-bold mb-2 ${paymentConfirmed ? 'text-green-800' : 'text-primary-dark'}`}>
                  {paymentConfirmed ? 'Payment confirmed' : 'Payment code created'}
                </h4>
                <p className="text-gray-700 mb-3">
                  {paymentConfirmed
                    ? 'Your payment has been confirmed. You can finish the registration.'
                    : 'Use the USSD code below to complete payment with Monime. This page will update automatically after payment.'}
                </p>
                <div className={`bg-white border ${paymentConfirmed ? 'border-green-200' : 'border-blue-200'} rounded-md p-4`}>
                  <p className="text-sm text-gray-500">USSD Code</p>
                  {paymentCode.ussd_code ? (
                    <>
                      <a
                        href={createUssdTelHref(paymentCode.ussd_code)}
                        className="text-2xl font-bold text-primary-dark break-all underline decoration-primary decoration-2 underline-offset-4"
                        aria-label="Open payment code in phone dialer"
                      >
                        {paymentCode.ussd_code}
                      </a>
                      <p className="text-sm text-gray-500 mt-2">Tap the code to open your phone dialer.</p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold text-primary-dark break-all">Code pending</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-sm text-gray-700">
                  <p><span className="font-semibold">Status:</span> {paymentStatus}</p>
                  <p><span className="font-semibold">Reference:</span> {paymentCode.reference}</p>
                  <p><span className="font-semibold">Amount:</span> {formatMoney(paymentCode.amount)}</p>
                  {paymentCode.expire_time && (
                    <p><span className="font-semibold">Expires:</span> {new Date(paymentCode.expire_time).toLocaleString()}</p>
                  )}
                </div>
                {!paymentConfirmed && !STOP_POLLING_PAYMENT_STATUSES.includes(paymentStatus) && (
                  <p className="text-sm text-gray-600 mt-4">
                    Checking payment status automatically...
                  </p>
                )}
                <div className="mt-5">
                  <Button
                    variant="primary"
                    onClick={() => setShowConfirmation(true)}
                    disabled={!paymentConfirmed}
                  >
                    {paymentConfirmed ? 'Finish Registration' : 'Waiting for Payment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            size="lg"
            onClick={handleCreatePaymentCode}
            disabled={isCreatingPaymentCode}
            icon={<FaMoneyBillWave />}
          >
            {isCreatingPaymentCode ? 'Creating Payment Code...' : 'Generate Payment Code'}
          </Button>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderIntro();
      case 2:
        return renderPersonalInformation();
      case 3:
        return renderAcademicBackground();
      case 4:
        return renderTrainingSelection();
      case 5:
        return renderPaymentStep();
      default:
        return renderIntro();
    }
  };

  const renderConfirmation = () => (
    <motion.div
      className="max-w-3xl mx-auto text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="bg-green-50 border-2 border-green-100 rounded-lg p-8 mb-8">
        <div className="bg-green-100 h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <FaCheckCircle className="text-green-600 text-4xl" />
        </div>
        <Heading level={2} className="text-green-800 mb-4">
          Registration Submitted Successfully!
        </Heading>
        {registrationSnapshot?.registrationNumber && (
          <p className="text-xl font-bold text-primary-dark mb-3">
            Registration ID: {registrationSnapshot.registrationNumber}
          </p>
        )}
        <p className="text-lg text-gray-700 mb-6">
          Thank you for registering for Afrovate Tech Hub's skills training program. We have received your information.
        </p>
        <div className="bg-white rounded-lg p-6 mb-6 text-left">
          <div className="flex items-center mb-4">
            <FaCalendarAlt className="text-primary mr-3" />
            <div>
              <h4 className="font-bold">Next Steps</h4>
              <p className="text-gray-600">Our team will contact you once payment is confirmed.</p>
            </div>
          </div>
          <div className="flex items-center">
            <FaEnvelope className="text-primary mr-3" />
            <div>
              <h4 className="font-bold">Contact</h4>
              <p className="text-gray-600">For questions, email info@afrovatesl.com.</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button variant="outline-primary" size="lg" to="/">Return to Homepage</Button>
          <Button variant="primary" size="lg" onClick={handleStartNewRegistration}>Start New Registration</Button>
        </div>
      </div>
    </motion.div>
  );

  const renderProgressIndicator = () => {
    const steps = [
      { number: 1, label: 'Start' },
      { number: 2, label: 'Personal' },
      { number: 3, label: 'Background' },
      { number: 4, label: 'Training' },
      { number: 5, label: 'Payment' }
    ];

    return (
      <div className="mb-10">
        <div className="hidden md:flex justify-between">
          {steps.map((stepItem) => (
            <div key={stepItem.number} className="flex flex-col items-center relative">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                step >= stepItem.number ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > stepItem.number ? <FaCheckCircle /> : stepItem.number}
              </div>
              <div className={`text-sm mt-2 ${step >= stepItem.number ? 'text-primary font-medium' : 'text-gray-500'}`}>
                {stepItem.label}
              </div>
              {stepItem.number < steps.length && (
                <div
                  className={`absolute top-4 left-0 h-0.5 -z-10 ${step > stepItem.number ? 'bg-primary' : 'bg-gray-200'}`}
                  style={{ width: 'calc(100% - 2rem)', left: '50%' }}
                ></div>
              )}
            </div>
          ))}
        </div>
        <div className="md:hidden">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium text-primary">Page {step} of 5</div>
            <div className="text-sm text-gray-500">{steps.find((item) => item.number === step)?.label}</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-primary h-2.5 rounded-full" style={{ width: `${(step / 5) * 100}%` }}></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Registration - Afrovate Tech Hub</title>
        <meta name="description" content="Register for Afrovate Tech Hub's 2-month skills training program." />
      </Helmet>

      <section
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/about/about-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-80"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Registration</span>
          </div>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Skills Training Registration
            </h1>
            <p className="text-xl text-white text-opacity-90 max-w-3xl mx-auto">
              Register for Afrovate Tech Hub's 2-month practical skills training program.
            </p>
          </div>
        </div>
      </section>

      <Section background="white" padding="lg">
        {showConfirmation ? (
          renderConfirmation()
        ) : (
          <div ref={formContainerRef} id="application-form-container">
            {renderProgressIndicator()}
            {renderStepContent()}
          </div>
        )}
      </Section>

      {!showConfirmation && (
        <Section background="gray" padding="normal">
          <div className="max-w-5xl mx-auto">
            <Heading level={2} align="center" className="mb-8" withAccent>
              Registration Process
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center">
                <div className="bg-primary bg-opacity-10 h-16 w-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaFileAlt className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-3">1. Submit Details</h3>
                <p className="text-gray-600">Complete the registration form with accurate personal and academic information.</p>
              </Card>
              <Card className="text-center">
                <div className="bg-primary bg-opacity-10 h-16 w-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaUsers className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-3">2. Secure Your Spot</h3>
                <p className="text-gray-600">Pay the NLe 450 registration fee to process your trainee ID and course manual.</p>
              </Card>
              <Card className="text-center">
                <div className="bg-primary bg-opacity-10 h-16 w-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaUserGraduate className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-3">3. Start Training</h3>
                <p className="text-gray-600">Receive cohort details and begin practical training at 20A Hennessy Street, Kingtom.</p>
              </Card>
            </div>
          </div>
        </Section>
      )}
    </>
  );
};

export default ApplyPage;
